// In the Timing words and Muito examples, **double asterisks** mark the focus word, shown in bold.
// Data for the Grammar tab's "Nuances" topic (Express with precision): three
// small clusters of words that are easy to use roughly but harder to use well.
// Each cluster is one card with a one-line "decision" question and a table.

// [Portuguese, English]
export type NuanceExample = [string, string]

// ── Cluster 1: Timing words (já / ainda) ────────────────────────────────────
export const TIMING_DECISION = 'Has it happened, is it still going, or hasn’t it happened yet?'

// Which side of the já / ainda split a use belongs to ("no longer" is não… mais, but it
// answers the same question as ainda: is it still going?).
export type TimingColumn = 'já' | 'ainda'
export const TIMING_COLUMNS: TimingColumn[] = ['já', 'ainda']

export type TimingRow = { english: string; portuguese: string; column: TimingColumn; examples: NuanceExample[] }

export const TIMING_ROWS: TimingRow[] = [
  { english: 'already', portuguese: 'já', column: 'já', examples: [['**Já** comi.', 'I already ate.']] },
  {
    english: 'right away / on my way',
    portuguese: 'já',
    column: 'já',
    examples: [
      ['**Já** vou!', 'Coming!'],
      ['**Já** volto.', 'Be right back.'],
    ],
  },
  { english: 'ever (in questions)', portuguese: 'já', column: 'já', examples: [['Você **já** foi ao Brasil?', 'Have you ever been to Brazil?']] },
  { english: 'not yet', portuguese: 'ainda não', column: 'ainda', examples: [['**Ainda não** comi.', "I haven't eaten yet."]] },
  { english: 'still', portuguese: 'ainda', column: 'ainda', examples: [['**Ainda** estou no trabalho.', "I'm still at work."]] },
  { english: 'no longer', portuguese: 'não... mais', column: 'ainda', examples: [['**Não** moro **mais** aqui.', "I don't live here anymore."]] },
]

// ── Cluster 2: Diminutives ──────────────────────────────────────────────────
export const DIMINUTIVE_DECISION = 'Diminutives (-inho/-inha, -zinho/-zinha) carry feeling, not just size.'

export type DiminutiveRow = {
  effect: string
  example: string
  base: string
  rule: string
  // Only what the spelling test needs to rebuild the example from the base.
  feminine?: boolean
}

export const DIMINUTIVE_ROWS: DiminutiveRow[] = [
  { effect: 'Softens a quantity', example: 'pouquinho', base: 'pouco', rule: '-o → -inho, c → qu' },
  { effect: 'Warms an offer', example: 'cafezinho', base: 'café', rule: '+ -zinho, drop the accent' },
  { effect: 'Adds immediacy', example: 'agorinha', base: 'agora', rule: '-a → -inha', feminine: true },
  { effect: 'Downplays', example: 'minutinho', base: 'minuto', rule: '-o → -inho' },
  { effect: 'Actual size', example: 'casinha', base: 'casa', rule: '-a → -inha', feminine: true },
  { effect: 'Affection', example: 'amiguinho', base: 'amigo', rule: '-o → -inho, g → gu' },
  { effect: 'Softens / affection', example: 'florzinha', base: 'flor', rule: '+ -zinha', feminine: true },
]

// ── Cluster 3: Muito ────────────────────────────────────────────────────────
export const MUITO_DECISION = 'Does muito modify a noun, or something else?'

// Two columns, like já / ainda: what muito modifies, and what that does to it.
export type MuitoColumn = {
  id: 'fixed' | 'agrees'
  // The column heading: what muito modifies, and whether it changes.
  title: string
  rows: Array<{ use: string; example: NuanceExample }>
}

export const MUITO_COLUMNS: MuitoColumn[] = [
  {
    id: 'fixed',
    title: 'With an adjective, adverb or verb — never changes',
    rows: [
      { use: 'adjective', example: ['Ela é **muito** bonita.', 'She is very pretty.'] },
      { use: 'adverb', example: ['Ele fala **muito** bem.', 'He speaks very well.'] },
      { use: 'verb', example: ['Gosto **muito** disso.', 'I like that a lot.'] },
    ],
  },
  {
    id: 'agrees',
    title: 'With a noun — agrees in gender and number',
    rows: [
      { use: 'masc. sing.', example: ['**muito** tempo', 'a lot of time'] },
      { use: 'fem. sing.', example: ['**muita** gente', 'a lot of people'] },
      { use: 'masc. pl.', example: ['**muitos** livros', 'many books'] },
      { use: 'fem. pl.', example: ['**muitas** pessoas', 'many people'] },
    ],
  },
]

// ── velho / antigo ─────────────────────────────────────────────────────────
export const OLD_DECISION = 'Is it simply not new, or does it belong to an earlier time?'
export const OLD_QUICK_TEST = 'Quick test: if “new” is the opposite, use velho; if “modern” or “former” fits, use antigo.'

export type OldWord = 'velho' | 'antigo'
// The two columns: the word and, in grey beside it, what it means.
export const OLD_WORDS: Array<{ id: OldWord; note: string }> = [
  { id: 'velho', note: 'not new: aged or worn' },
  { id: 'antigo', note: 'from an earlier time' },
]

// `note` is the small grey line under the use. Position matters for both words: before the noun
// velho means long-time and antigo means former.
export type OldRow = { word: OldWord; use: string; note?: string; example: NuanceExample }

export const OLD_ROWS: OldRow[] = [
  { word: 'velho', use: 'Worn or used for a long time', example: ['Esse carro é **velho**, mas ainda funciona.', 'This car is old, but it still works.'] },
  { word: 'velho', use: 'A person’s age', note: 'blunt: idoso is politer', example: ['Meu avô é **velho**, mas muito ativo.', 'My grandfather is old, but very active.'] },
  {
    word: 'velho',
    use: 'Before the noun: long-time',
    note: 'after the noun: elderly (um amigo velho)',
    example: ['Ele é um **velho** amigo meu.', 'He is an old friend of mine.'],
  },
  { word: 'antigo', use: 'Vintage or historic', example: ['Ele coleciona carros **antigos**.', 'He collects classic cars.'] },
  {
    word: 'antigo',
    use: 'Before the noun: former',
    note: 'after the noun: old, historic (móveis antigos)',
    example: ['Encontrei meu **antigo** chefe ontem.', 'I ran into my former boss yesterday.'],
  },
  { word: 'antigo', use: 'Ancient times', example: ['Estudo a história da Grécia **antiga**.', 'I study the history of ancient Greece.'] },
]

// ── Cluster 4: aqui / aí / ali / lá ─────────────────────────────────────────
export const DEIXIS_DECISION = 'How far is it, and from whom?'

// Each place adverb matches one demonstrative series: este / isto, esse / isso, aquele / aquilo.
export type DeixisRow = {
  // The adverb(s) that share this distance.
  adverbs: string[]
  distance: string
  demonstratives: string
  examples: NuanceExample[]
}

export const DEIXIS_ROWS: DeixisRow[] = [
  {
    adverbs: ['aqui', 'cá'],
    distance: 'near the speaker',
    demonstratives: 'este / isto',
    examples: [
      ['Eu moro **aqui**.', 'I live here.'],
      ['Vem **cá**!', 'Come here!'],
    ],
  },
  {
    adverbs: ['aí'],
    distance: 'near the listener',
    demonstratives: 'esse / isso',
    examples: [['O que você tem **aí**?', 'What do you have there?']],
  },
  {
    adverbs: ['ali'],
    distance: 'over there, usually visible',
    demonstratives: 'aquele / aquilo',
    examples: [['O banheiro fica **ali**.', 'The bathroom is over there.']],
  },
  {
    adverbs: ['lá'],
    distance: 'far away, often not visible',
    demonstratives: 'aquele / aquilo',
    examples: [['Eu nunca fui **lá**.', "I've never been there."]],
  },
]
